import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock } from 'lucide-react';
import { useDashboardTab } from '@/hooks/useDashboardTab';
import { useUnreadMessages } from '@hooks/useUnreadMessages';
import { ChatNotificationsProvider } from '@/components/messaging/ChatNotificationsProvider';
import { TabNav } from '@molecules/TabNav';
import { subscriptionService } from '@services/subscriptionService';
import { BrowseSection } from './sections/BrowseSection';
import { OrdersSection } from './sections/OrdersSection';
import { AccountingSection } from './sections/AccountingSection';
import { RemindersSection } from './sections/RemindersSection';
import { SettingsSection } from './sections/SettingsSection';
import { MessagesSection } from './sections/MessagesSection';
import { SubscriptionSection } from './sections/SubscriptionSection';

const TABS = [
  { id: 'browse', label: 'Keşfet' },
  { id: 'orders', label: 'Siparişler' },
  { id: 'messages', label: 'Mesajlar' },
  { id: 'accounting', label: 'Muhasebe' },
  { id: 'reminders', label: 'Hatırlatıcılar' },
  { id: 'subscription', label: 'Üyelik' },
  { id: 'settings', label: 'Ayarlar' },
] as const;

const TAB_IDS = TABS.map((t) => t.id);

/** Tabs that stay reachable when the trial/subscription has expired. */
const PAYWALL_EXEMPT_TABS = new Set(['subscription', 'settings']);

export function RestaurantDashboard() {
  const { activeTab, setActiveTab } = useDashboardTab('browse', TAB_IDS);
  const queryClient = useQueryClient();

  const { data: unread } = useUnreadMessages();

  const { data: me } = useQuery({
    queryKey: ['restaurant', 'subscription', 'me'],
    queryFn: () => subscriptionService.getMySubscription(),
    staleTime: 60_000,
  });

  // A 402 from any API call means access just expired — refresh the state.
  useEffect(() => {
    const onRequired = () => {
      void queryClient.invalidateQueries({ queryKey: ['restaurant', 'subscription'] });
    };
    window.addEventListener('subscription:required', onRequired);
    return () => window.removeEventListener('subscription:required', onRequired);
  }, [queryClient]);

  const access = me?.access;
  const isBlocked = Boolean(me && access?.status === 'expired' && me.enforced);
  const showTrialBanner =
    access?.status === 'trial' &&
    typeof access.trialDaysLeft === 'number' &&
    access.trialDaysLeft <= 7;

  const effectiveTab = isBlocked && !PAYWALL_EXEMPT_TABS.has(activeTab) ? 'subscription' : activeTab;

  const tabsWithBadges = TABS.map((tab) =>
    tab.id === 'messages' ? { ...tab, badge: unread?.count } : tab,
  );

  return (
    <div>
      <ChatNotificationsProvider />
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Restoran Paneli</h1>

      {showTrialBanner && !isBlocked && (
        <button
          type="button"
          onClick={() => setActiveTab('subscription')}
          className="mb-4 flex w-full items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-left text-sm text-blue-900 transition-colors hover:bg-blue-100"
        >
          <CalendarClock className="h-5 w-5 shrink-0 text-blue-600" />
          <span>
            Deneme sürenizin bitmesine <strong>{access?.trialDaysLeft} gün</strong> kaldı. Kesintisiz
            kullanım için bir üyelik planı seçin →
          </span>
        </button>
      )}

      {isBlocked && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
          <span>
            Deneme süreniz sona erdi. Platformu kullanmaya devam etmek için bir üyelik planı
            seçmeniz gerekiyor.
          </span>
        </div>
      )}

      <TabNav
        tabs={tabsWithBadges}
        activeTab={effectiveTab}
        onChange={(tab) => {
          if (isBlocked && !PAYWALL_EXEMPT_TABS.has(tab)) {
            setActiveTab('subscription');
            return;
          }
          setActiveTab(tab);
        }}
      />
      <div className="mt-6">
        {effectiveTab === 'browse' && <BrowseSection />}
        {effectiveTab === 'orders' && <OrdersSection />}
        {effectiveTab === 'messages' && <MessagesSection />}
        {effectiveTab === 'accounting' && <AccountingSection />}
        {effectiveTab === 'reminders' && <RemindersSection />}
        {effectiveTab === 'subscription' && <SubscriptionSection />}
        {effectiveTab === 'settings' && <SettingsSection />}
      </div>
    </div>
  );
}
